import type { StudySelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { Tile } from '@components/shared/tile.component';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useUpdateMeiaStudy } from '@queries/meia.query';
import { type MeiaStudyContextFormValues, meiaStudyContextSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { EditIcon } from 'lucide-react';
import { type FC, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';

export type MeiaStudyLens = 'DM' | 'ME';

type MeiaContextTileProps = {
  canEdit: boolean;
  lens?: MeiaStudyLens;
  study: StudySelectDto;
};

const toNullableText = (value: string): string | null => {
  const nextValue = value.trim();
  return nextValue ? nextValue : null;
};

export const MeiaContextTile: FC<MeiaContextTileProps> = (props) => {
  const { canEdit: canEditProp, lens = 'ME', study } = props;
  // Phase-08 (Decision Maker) forbids ME authoring controls on DM surfaces. Hard-gate inside
  // the tile so callers cannot accidentally expose the edit affordance when rendering on a
  // DM-lens route, regardless of the caller's permission check.
  const canEdit = lens === 'DM' ? false : canEditProp;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasContext = Boolean(
    study.contextNarrative?.trim() || study.environment?.trim() || study.threatEnvelope?.trim(),
  );

  return (
    <>
      <Tile
        emptyState={{
          message: canEdit ? 'Add Context' : undefined,
          onClick: canEdit ? () => setIsModalOpen(true) : undefined,
          title: 'No Context',
        }}
        header={{
          iconButton: canEdit
            ? {
                icon: <EditIcon size={14} />,
                onClick: () => setIsModalOpen(true),
              }
            : undefined,
          title: 'Context',
        }}
        hoverReveal={canEdit}
        isEmpty={!hasContext}
      >
        <div className="flex flex-col gap-3 text-sm">
          {study.threatEnvelope && (
            <section className="flex flex-col gap-1">
              <h4 className="font-medium text-gray-700">Threat Envelope</h4>
              <p className="whitespace-pre-wrap text-gray-600">{study.threatEnvelope}</p>
            </section>
          )}
          {study.environment && (
            <section className="flex flex-col gap-1">
              <h4 className="font-medium text-gray-700">Environment</h4>
              <p className="whitespace-pre-wrap text-gray-600">{study.environment}</p>
            </section>
          )}
          {study.contextNarrative && (
            <section className="flex flex-col gap-1">
              <h4 className="font-medium text-gray-700">Narrative</h4>
              <p className="whitespace-pre-wrap text-gray-600">{study.contextNarrative}</p>
            </section>
          )}
        </div>
      </Tile>

      {canEdit && (
        <MeiaContextModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          study={study}
        />
      )}
    </>
  );
};

type MeiaContextModalProps = {
  isOpen: boolean;
  onClose: () => void;
  study: StudySelectDto;
};

const MeiaContextModal: FC<MeiaContextModalProps> = (props) => {
  const { isOpen, onClose, study } = props;
  const { isPending: isSaving, mutateAsync: updateStudy } = useUpdateMeiaStudy();

  const defaultValues: MeiaStudyContextFormValues = {
    contextNarrative: study.contextNarrative ?? '',
    environment: study.environment ?? '',
    threatEnvelope: study.threatEnvelope ?? '',
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const validationResult = meiaStudyContextSchema.safeParse(value);
      if (!validationResult.success) {
        errorToast({ description: 'Please review context fields.' });
        return;
      }

      await updateStudy({
        contextNarrative: toNullableText(validationResult.data.contextNarrative),
        environment: toNullableText(validationResult.data.environment),
        studyId: study.id,
        threatEnvelope: toNullableText(validationResult.data.threatEnvelope),
      });
      successToast({ description: 'Context saved.' });
      onClose();
    },
    onSubmitInvalid: createFormInvalidHandler<typeof defaultValues>(),
    validators: { onSubmit: meiaStudyContextSchema },
  });

  return (
    <Modal
      centered
      footer={{
        rightButton: {
          children: isSaving ? 'Saving...' : 'Save',
          disabled: isSaving,
          onClick: () => void form.handleSubmit(),
        },
      }}
      header={{ title: 'Context' }}
      isOpen={isOpen}
      onClose={onClose}
      width="640px"
    >
      <form
        {...MEIA_AUTOFILL_IGNORE_PROPS}
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.AppField name="threatEnvelope">
            {(field) => (
              <field.TextAreaField
                label="Threat Envelope"
                placeholder="Group-3 UAS swarm, loitering munition mix, SIGINT-cued C2 relay."
                rows={5}
              />
            )}
          </form.AppField>
          <form.AppField name="environment">
            {(field) => (
              <field.TextAreaField
                label="Environment"
                placeholder="Tropical maritime; congested RF environment; coastal mixed open and jungle terrain."
                rows={5}
              />
            )}
          </form.AppField>
          <form.AppField name="contextNarrative">
            {(field) => (
              <field.TextAreaField
                label="Narrative"
                placeholder="Adversary ISR overflight tempo elevated last 30 days."
                rows={5}
              />
            )}
          </form.AppField>
        </form.AppForm>
      </form>
    </Modal>
  );
};
